/** Detects HTML error pages returned by an intermediary instead of a JSON API response. */
export function isHtmlErrorBody(text: string): boolean {
  const trimmed = text.trim().toLowerCase();
  return trimmed.startsWith("<!doctype html") || trimmed.startsWith("<html") || trimmed.includes("<body");
}

function messageFromJson(data: unknown): string | undefined {
  if (!data || typeof data !== "object") return undefined;
  const record = data as Record<string, unknown>;
  if (typeof record.message === "string" && record.message) return record.message;
  if (typeof record.error === "string" && record.error) return record.error;
  const firstError = Array.isArray(record.errors) ? record.errors[0] : undefined;
  if (firstError && typeof firstError === "object") {
    const message = (firstError as { message?: unknown }).message;
    if (typeof message === "string" && message) return message;
  }
  return undefined;
}

function normalizePlainTextError(status: number, text: string): string {
  const lowerText = text.toLowerCase();
  if (status === 429 || lowerText.includes("error 1015") || lowerText.includes("rate limited")) {
    return "Finster API is rate limiting requests. Please wait a moment and try again.";
  }
  if (isHtmlErrorBody(text)) {
    return `Request failed with HTTP ${status}. The upstream service returned an HTML error page.`;
  }
  return text || `HTTP ${status}`;
}

/**
 * Same handling as {@link parseErrorResponse} for callers that have already read
 * the body, so a response body is never consumed twice.
 */
export function parseErrorBodyText(status: number, text: string): string {
  try {
    const message = messageFromJson(JSON.parse(text));
    if (message) return message;
  } catch {
    // Fall through to text.
  }

  return normalizePlainTextError(status, text);
}

export async function parseErrorResponse(response: Response): Promise<string> {
  try {
    const message = messageFromJson(await response.clone().json());
    if (message) return message;
  } catch {
    // Fall through to text.
  }

  try {
    const text = await response.text();
    return normalizePlainTextError(response.status, text);
  } catch {
    return `HTTP ${response.status}`;
  }
}

export const readApiError = parseErrorResponse;
